import { Button, Checkbox, Col, Divider, Form, InputNumber, Row, Typography, message } from 'antd';
import type { FormRule } from 'antd';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi } from '../contexts/ApiContext';
import type { LoyaltyCategorySetting, LoyaltySettingsPayload } from '../domain/loyaltySettings';

const { Text } = Typography;

type CategoryValues = Omit<LoyaltyCategorySetting, 'name'>;

interface LoyaltyFormValues {
    categories?: Partial<CategoryValues>[];
    standardDiscountPercent?: number;
    standardDiscountEnabled?: boolean;
}

/** The category's upper bound must lie above its lower bound, or its range is empty. */
const upperBoundRule =
    (index: number, errorMessage: string): FormRule =>
    ({ getFieldValue }) => ({
        validator: (_rule, value: number | null) => {
            const lowerBound: number | null = getFieldValue([
                'categories',
                index,
                'lowerBound',
            ]);
            return value === null || lowerBound === null || value > lowerBound
                ? Promise.resolve()
                : Promise.reject(new Error(errorMessage));
        },
    });

const toPayloadCategory = (
    name: string,
    values: Partial<CategoryValues> | undefined,
): LoyaltyCategorySetting => ({
    name,
    discountPercent: values?.discountPercent ?? 0,
    lowerBound: values?.lowerBound ?? 0,
    upperBound: values?.upperBound ?? 0,
});

function CategoryFields({
    index,
    name,
    t,
}: Readonly<{ index: number; name: string; t: TFunction }>) {
    return (
        <>
            <Divider orientation="left">{name}</Divider>
            <Row gutter={16}>
                <Col span={8}>
                    <Form.Item
                        name={['categories', index, 'discountPercent']}
                        label={t('configuration.loyalty.discount')}
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={100}
                            precision={0}
                            data-testid={`loyalty-discount-${name}`}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name={['categories', index, 'lowerBound']}
                        label={t('configuration.loyalty.lowerBound')}
                        tooltip={t('configuration.loyalty.boundsHelp')}
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            precision={0}
                            data-testid={`loyalty-lower-${name}`}
                        />
                    </Form.Item>
                </Col>
                <Col span={8}>
                    <Form.Item
                        name={['categories', index, 'upperBound']}
                        label={t('configuration.loyalty.upperBound')}
                        dependencies={[['categories', index, 'lowerBound']]}
                        rules={[
                            { required: true },
                            upperBoundRule(index, t('configuration.loyalty.upperBoundTooLow')),
                        ]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            precision={0}
                            data-testid={`loyalty-upper-${name}`}
                        />
                    </Form.Item>
                </Col>
            </Row>
        </>
    );
}

/**
 * The Loyalty tab's categories and standard discount, where both programs keep them: the
 * categories in Delphi's `bonus` table (every row, Diamond included; the names are data, stored on
 * the clients, so they are shown as stored and cannot be renamed here), the standard discount in
 * the shop-wide `Standard` / `aplica_reducere_standard` rows Add Contract starts from.
 */
export default function LoyaltySettings() {
    const { t } = useTranslation();
    const { configuration } = useApi();
    const [form] = Form.useForm<LoyaltyFormValues>();
    const [categoryNames, setCategoryNames] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);

    const show = useCallback(
        (settings: LoyaltySettingsPayload) => {
            setCategoryNames(settings.categories.map(category => category.name));
            form.setFieldsValue(settings);
        },
        [form],
    );

    useEffect(() => {
        void configuration.getLoyalty().then(response => {
            if (!response.success || response.data === undefined) {
                message.error(response.error ?? t('configuration.loyalty.loadError'));
                return;
            }
            show(response.data);
        });
    }, [configuration, show, t]);

    const save = async (values: LoyaltyFormValues) => {
        setSaving(true);
        try {
            const response = await configuration.saveLoyalty({
                categories: categoryNames.map((name, index) =>
                    toPayloadCategory(name, values.categories?.[index]),
                ),
                standardDiscountPercent: values.standardDiscountPercent ?? 0,
                standardDiscountEnabled: values.standardDiscountEnabled === true,
            });
            if (!response.success || response.data === undefined) {
                message.error(response.error ?? t('configuration.loyalty.saveError'));
                return;
            }
            show(response.data);
            message.success(t('configuration.saveSuccess'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Form
            form={form}
            layout="vertical"
            onFinish={values => void save(values)}
            data-testid="loyalty-form"
        >
            <Text type="secondary">{t('configuration.loyalty.categoriesHint')}</Text>
            {categoryNames.map((name, index) => (
                <CategoryFields key={name} index={index} name={name} t={t} />
            ))}

            <Divider />
            <Row gutter={16}>
                <Col span={12}>
                    <Form.Item
                        name="standardDiscountPercent"
                        label={t('configuration.loyalty.standardDiscount')}
                        tooltip={t('configuration.loyalty.standardDiscountHelp')}
                        rules={[{ required: true }]}
                    >
                        <InputNumber
                            style={{ width: '100%' }}
                            min={0}
                            max={100}
                            precision={0}
                            data-testid="loyalty-standard-discount"
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="standardDiscountEnabled" valuePropName="checked">
                        <Checkbox data-testid="loyalty-standard-enabled">
                            {t('configuration.loyalty.applyStandardDiscount')}
                        </Checkbox>
                    </Form.Item>
                </Col>
            </Row>
            <Form.Item>
                <Button type="primary" htmlType="submit" loading={saving} data-testid="save-loyalty">
                    {t('configuration.save')}
                </Button>
            </Form.Item>
        </Form>
    );
}
